import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {ReactNode, useState} from 'react';

import {groupBy, isNotNil} from 'ramda';

import {
  OnlineOfferEquipmentCategory,
  OnlineOfferEquipmentItem,
} from '../../../api/types/onlineOfferAlternativesTypes';
import {Button} from '../../../components/Button/Button';
import {CardContainer} from '../../../components/CardContainer/CardContainer';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';

/** Standard equipment lists run long; the extras above it are what tells alternatives apart. */
const COLLAPSED_STANDARD_COUNT = 12;

interface AlternativeEquipmentProps {
  equipment: OnlineOfferEquipmentItem[];
}

const byName = (a: OnlineOfferEquipmentItem, b: OnlineOfferEquipmentItem) =>
  a.name.localeCompare(b.name);

const ofCategory = (
  equipment: OnlineOfferEquipmentItem[],
  category: OnlineOfferEquipmentCategory
) => equipment.filter((item) => item.category === category).sort(byName);

export function AlternativeEquipment(props: AlternativeEquipmentProps) {
  const formatCurrency = useFormatCurrency();
  const [isStandardExpanded, setIsStandardExpanded] = useState(false);

  const packages = Object.entries(
    groupBy(
      (item) => item.packageName ?? t('alternatives.equipment.aPackage', 'a package'),
      ofCategory(props.equipment, 'PACKAGE')
    )
  );
  const options = ofCategory(props.equipment, 'OPTION');
  const standard = ofCategory(props.equipment, 'STANDARD');
  const visibleStandard = isStandardExpanded
    ? standard
    : standard.slice(0, COLLAPSED_STANDARD_COUNT);

  return (
    <CardContainer heading={t('alternatives.detail.equipment', 'Equipment')}>
      <VStack spacing={8}>
        <Show when={packages.length > 0}>
          <EquipmentGroup title={t('alternatives.detail.packages', 'Packages')}>
            {packages.map(([packageName, items]) => (
              <VStack key={packageName} spacing={1}>
                <Text size="small" alternative>
                  {packageName}
                </Text>
                <Text size="small" color="secondary">
                  {(items ?? []).map((item) => item.name).join(', ')}
                </Text>
              </VStack>
            ))}
          </EquipmentGroup>
        </Show>
        <Show when={options.length > 0}>
          <EquipmentGroup title={t('alternatives.detail.options', 'Options')}>
            {options.map((item) => (
              <HStack key={item.key} justify="space-between" spacing={4}>
                <Text size="small">{item.name}</Text>
                <Show when={isNotNil(item.price)}>
                  <Text size="small" color="secondary" noWrap>
                    {item.price
                      ? formatCurrency(Number(item.price.amount), item.price.currency)
                      : null}
                  </Text>
                </Show>
              </HStack>
            ))}
          </EquipmentGroup>
        </Show>
        <Show when={standard.length > 0}>
          <EquipmentGroup
            title={t('alternatives.detail.standard', 'Standard equipment')}
            columns={[1, 2]}
          >
            {visibleStandard.map((item) => (
              <Text key={item.key} size="small">
                {item.name}
              </Text>
            ))}
          </EquipmentGroup>
          <Show when={standard.length > COLLAPSED_STANDARD_COUNT}>
            <HStack>
              <Button
                variant="link"
                size="small"
                title={
                  isStandardExpanded
                    ? t('alternatives.detail.showLess', 'Show less')
                    : t('alternatives.detail.showAll', 'Show all {count}', {
                        count: standard.length,
                      })
                }
                onClick={() => setIsStandardExpanded(!isStandardExpanded)}
                data-testid="alternativeEquipment-toggle"
              />
            </HStack>
          </Show>
        </Show>
      </VStack>
    </CardContainer>
  );
}

interface EquipmentGroupProps {
  title: string;
  columns?: 1 | [1, 2];
  children: ReactNode;
}

function EquipmentGroup(props: EquipmentGroupProps) {
  return (
    <VStack spacing={3}>
      <Text size="xSmall" alternative color="secondary">
        {props.title}
      </Text>
      <Grid columns={props.columns ?? 1} spacing={8} verticalSpacing={3}>
        {props.children}
      </Grid>
    </VStack>
  );
}
